import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask } from "Models/despatch.task";
import { Fleet } from "Models/fleet";
import { Member } from "Models/members/member";
import { StorageBox } from "Models/storage.box";
import { Team } from "Models/team";
import { ResourceViewerOptions } from "Util/enums";

export enum DNDSlotTypes {
  CREW_LEADER = "crewLeader",
  CREW_EQUIPMENTS = "crewEquipments",
  CREW_STAFFS = "crewStaffs",
  CREW_STORAGE_BOXES = "crewStorageBoxes",
  CREW_ORDERS = "crewTasks",

  ORDER_LEADER = "orderLeader",
  ORDER_EQUIPMENTS = "orderEquipments",
  ORDER_STAFFS = "orderStaffs",
  ORDER_STORAGE_BOXES = "orderStorageBoxes",
  ORDER_TEAM = "orderTeam",
}

export enum DNDSourceTypes {
  CREW_ORDER = "crewTasks",
  CREW_LEADER = "crewLeader",
  CREW_MEMBER = "crewMember",
  CREW_EQUIPMENT = "crewEquipment",
  CREW_TEAM = "crewTeam",
  CREW_STORAGE_BOX = "crewStorageBox",
  CREW_TASK = "crewTask",

  ORDER_LEADER = "orderLeader",
  ORDER_MEMBER = "orderMembers",
  ORDER_EQUIPMENT = "orderEquipment",
  ORDER_TEAM = "orderTeam",
  ORDER_STORAGE_BOX = "orderStorageBox",
}

export enum DNDFieldsNames {
  LIST_ORDERS = "listOrders",
  LIST_STORAGE_BOXES = "listStorageBoxex",
  LIST_FLEETS = "listFleets",
  LIST_MEMBERS = "listMembers",
  LIST_TEAMS = "listTeams",
  LIST_CREWS = "listCrews",
  CARD_TEAM = "cardTeam",
  CARD_ORDER = "cardOrder",
}

export interface GuardSlotSourceElementParams {
  sourceElement: DespatchTask | DespatchCrew | Team | Member | Fleet | StorageBox;
  slotElement: DespatchTask | DespatchCrew;
  draggableField: string;
  droppableField: string;
}

export interface GuardFieldParams {
  sourceElement: GuardSlotSourceElementParams["sourceElement"] | Member[] | Fleet[];
  slotElement: GuardSlotSourceElementParams["slotElement"];

  destinyFieldName: string;
  originFieldName?: string;
}
export interface DNDSlotsConfig {
  fields: { destinyName: string; originName?: string; defaultValue: any }[];
  shouldUpdateSourceField?: boolean;
  // fieldName: string;
  // defaultValue: any;
  validSources: DNDSourceTypes[];
  onBeforeUpdateGuard?: (params: GuardSlotSourceElementParams) => void | false;
  onBeforeUpdateFieldGuard?: (params: GuardFieldParams) => void | false;
}

interface ViewerConfigType {
  slots: Partial<Record<DNDSlotTypes, DNDSlotsConfig>>;
}
interface ViewerConfigsType {
  [ResourceViewerOptions.TEAM]: ViewerConfigType;
  [ResourceViewerOptions.ORDER]: ViewerConfigType;
}

export const VIEWER_CONFIGS: ViewerConfigsType = {
  [ResourceViewerOptions.TEAM]: {
    slots: {
      [DNDSlotTypes.CREW_LEADER]: {
        fields: [
          {
            destinyName: "memberLeader",
            defaultValue: null,
          },
        ],
        validSources: [DNDSourceTypes.CREW_LEADER],
        onBeforeUpdateGuard({ slotElement, sourceElement }) {
          const slotValue = slotElement as DespatchCrew;
          const sourceValue = sourceElement as Member;

          if (
            slotValue.memberLeader?.id === sourceValue.id! ||
            slotValue.members?.find((member) => member.id === sourceValue.id!)
          )
            return false;
        },
      },
      [DNDSlotTypes.CREW_STAFFS]: {
        fields: [
          {
            destinyName: "members",
            defaultValue: [],
          },
        ],
        validSources: [DNDSourceTypes.CREW_LEADER, DNDSourceTypes.CREW_MEMBER],
        onBeforeUpdateGuard({ slotElement, sourceElement }) {
          const slotValue = slotElement as DespatchCrew;
          const sourceValue = sourceElement as Member;

          if (
            slotValue.memberLeader?.id === sourceValue.id! ||
            slotValue.members?.find((member) => member.id === sourceValue.id!)
          )
            return false;
        },
      },
      [DNDSlotTypes.CREW_EQUIPMENTS]: {
        fields: [
          {
            destinyName: "fleets",
            defaultValue: [],
          },
        ],
        validSources: [DNDSourceTypes.CREW_EQUIPMENT],
      },
      [DNDSlotTypes.CREW_STORAGE_BOXES]: {
        fields: [
          {
            destinyName: "storageBoxes",
            defaultValue: [],
          },
        ],
        validSources: [DNDSourceTypes.CREW_STORAGE_BOX],
      },
      [DNDSlotTypes.CREW_ORDERS]: {
        fields: [
          {
            destinyName: "tasks",
            defaultValue: [],
          },
        ],
        shouldUpdateSourceField: true,
        validSources: [DNDSourceTypes.CREW_ORDER],
        onBeforeUpdateGuard({ slotElement, sourceElement }) {
          const slotValue = slotElement as DespatchCrew;
          const sourceValue = sourceElement as DespatchTask;

          if (slotValue?.tasks?.find((task) => task.id === sourceValue?.id)) return false;
        },
      },
    },
  },
  [ResourceViewerOptions.ORDER]: {
    slots: {
      [DNDSlotTypes.ORDER_TEAM]: {
        fields: [
          {
            originName: "memberLeader",
            destinyName: "team.memberLeader",
            defaultValue: null,
          },
          {
            originName: "members",
            destinyName: "team.members",
            defaultValue: [],
          },
          {
            originName: "fleets",
            destinyName: "team.fleets",
            defaultValue: [],
          },
        ],
        validSources: [DNDSourceTypes.ORDER_TEAM],
        onBeforeUpdateFieldGuard(params) {
          const slotValue = params.slotElement as DespatchTask;

          const handlers = {
            "team.memberLeader": (leaderMember: Member | null) => {
              if (!leaderMember) return false;

              const team = slotValue.teams?.find((t) => t.id === slotValue.team?.id);

              if (
                team?.memberLeader?.id === leaderMember.id! ||
                team?.members?.find((member) => member.id === leaderMember.id!)
              )
                return false;
            },
            "team.members": (members: Member[]) => {
              if (!members?.length) return false;
              const memberIds = members.map((member) => member.id!);

              const team = slotValue.teams?.find((t) => t.id === slotValue.team?.id);

              (params.sourceElement as Member[]) = members.filter((member) => {
                return (
                  member.id! === team?.memberLeader?.id! ||
                  team?.members?.find((member) => memberIds.includes(member.id!))
                );
              });
            },
          };

          return handlers[params.destinyFieldName as keyof typeof handlers]?.(
            params.sourceElement as any
          );
        },
      },
      [DNDSlotTypes.ORDER_LEADER]: {
        fields: [
          {
            destinyName: "team.memberLeader",
            defaultValue: null,
          },
        ],
        validSources: [DNDSourceTypes.ORDER_LEADER],
        onBeforeUpdateGuard({ slotElement, sourceElement }) {
          const slotValue = slotElement as DespatchTask;
          const sourceValue = sourceElement as Member;

          const team = slotValue.teams?.find((t) => t.id === slotValue.team?.id);

          if (
            team?.memberLeader?.id === sourceValue.id! ||
            team?.members?.find((member) => member.id === sourceValue.id!)
          )
            return false;
        },
      },
      [DNDSlotTypes.ORDER_STAFFS]: {
        fields: [
          {
            destinyName: "team.members",
            defaultValue: [],
          },
        ],
        validSources: [DNDSourceTypes.ORDER_LEADER, DNDSourceTypes.ORDER_MEMBER],
        onBeforeUpdateGuard({ slotElement, sourceElement }) {
          const slotValue = slotElement as DespatchTask;
          const sourceValue = sourceElement as Member;

          const team = slotValue.teams?.find((t) => t.id === slotValue.team?.id);

          if (
            team?.memberLeader?.id === sourceValue.id! ||
            team?.members?.find((member) => member.id === sourceValue.id!)
          )
            return false;
        },
      },
      [DNDSlotTypes.ORDER_EQUIPMENTS]: {
        fields: [
          {
            destinyName: "team.fleets",
            defaultValue: [],
          },
        ],
        validSources: [DNDSourceTypes.ORDER_EQUIPMENT],
      },
      [DNDSlotTypes.ORDER_STORAGE_BOXES]: {
        fields: [
          {
            destinyName: "storageBoxes",
            defaultValue: [],
          },
        ],
        validSources: [DNDSourceTypes.ORDER_STORAGE_BOX],
      },
    },
  },
};

export const getCurrentSlotConfig = (viewer: ResourceViewerOptions, slotName: DNDSlotTypes) => {
  return VIEWER_CONFIGS[viewer].slots[slotName];
};

export const generateSlotSourceId = (
  id: string | number,
  type: "source" | "slot",
  fieldName: DNDFieldsNames,
  sourceOrSlotName: DNDSourceTypes | DNDSlotTypes
) => {
  return `${fieldName}/${id}/${sourceOrSlotName}/${type}`;
};

// REGEX FOR ANALYSE SLOTS AND SOURCES
// {fieldName}/{id}/{name}/{"source" | "slot"}

const REGEX_SLOT_AND_SOURCE_ID = /^([^/]+)\/(\d+)\/([^/]+)\/(source|slot)$/;

export type AnalyzerSlotSourceIdReturnType =
  | {
      isValid: true;
      fieldName: DNDFieldsNames;
      id: number;
      type: "source";
      name: DNDSourceTypes;
    }
  | {
      isValid: true;
      fieldName: DNDFieldsNames;
      id: number;
      type: "slot";
      name: DNDSlotTypes;
    }
  | {
      isValid: false;
      fieldName: null;
      type: null;
      id: null;
      name: null;
    };

export const analyzerSlotSourceId = (value: string): AnalyzerSlotSourceIdReturnType => {
  const match = value.match(REGEX_SLOT_AND_SOURCE_ID);

  if (match) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention, unused-imports/no-unused-vars
    const [_, fieldName, id, name, type] = match;

    return {
      isValid: true,
      fieldName,
      id: Number(id),
      type,
      name,
    } as AnalyzerSlotSourceIdReturnType;
  }

  return {
    isValid: false,
    fieldName: null,
    id: null,
    type: null,
    name: null,
  };
};
